import AddRounded from '@mui/icons-material/AddRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import PersonAddOutlined from '@mui/icons-material/PersonAddOutlined';
import type { GridColDef } from '@mui/x-data-grid-pro';
import { BaseModal } from '@src/common/components/BaseModal';
import { Button } from '@src/common/components/button/Button';
import { FilterableTable } from '@src/common/components/FilterableTable';
import { IconButton } from '@src/common/components/IconButton';
import { LoadingSpinner } from '@src/common/components/LoadingSpinner';
import { CloseConfirmModalFooter } from '@src/common/components/modal/CloseConfirmModalFooter';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { useToast } from '@src/common/hooks/useToast';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { AddDriverForm } from '@src/driver/AddDriverForm';
import type { DriverListing } from '@src/driver/domain/DriverList';
import { EditDriverForm } from '@src/driver/EditDriverForm';
import { useDeleteDriver } from '@src/driver/hooks/useDeleteDriver';
import { useDrivers } from '@src/driver/hooks/useDrivers';
import { t } from 'i18next';
import { useState } from 'react';

type Props = {
  clientId: string;
};

export const DriversList = ({ clientId }: Props) => {
  const {
    drivers: { drivers },
    isLoading,
  } = useDrivers(clientId);

  type Actions = 'add' | 'update';

  const [openModal, setOpenModal] = useState(false);
  const [selectedDriver, setSelectedDriver] = useState<DriverListing | null>(
    null,
  );
  const [action, setAction] = useState<Actions | null>(null);

  const openAddDriverModal = () => {
    setAction('add');
    setOpenModal(true);
  };

  const openEditDriverModal = (driver: DriverListing) => {
    setSelectedDriver(driver);
    setAction('update');
    setOpenModal(true);
  };

  const isEmptyList = drivers.length < 1;

  const getForm = () => {
    if (action == 'add') {
      return (
        <AddDriverForm
          clientId={clientId}
          onCreate={() => setOpenModal(false)}
        />
      );
    }
    if (action == 'update' && selectedDriver) {
      return (
        <EditDriverForm
          clientId={clientId}
          driver={selectedDriver}
          onUpdate={() => setOpenModal(false)}
        />
      );
    }
  };

  if (isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <LoadingSpinner />
      </Stack>
    );
  }

  const columns: GridColDef[] = [
    { field: 'name', headerName: t('name'), flex: 1 },
    { field: 'email', headerName: t('email'), flex: 1 },
    { field: 'phone', headerName: t('phone'), flex: 1 },
    {
      field: 'Actions',
      headerName: '',
      filterable: false,
      sortable: false,
      disableColumnMenu: true,
      hideable: false,
      renderCell: (params) => {
        return (
          <>
            <IconButton
              onClick={() => openEditDriverModal(params.row)}
              title={t('editDriver')}
            >
              <Edit />
            </IconButton>
            <DeleteDriverButton clientId={clientId} driverId={params.row.id} />
          </>
        );
      },
    },
  ];

  const rows = drivers.map((driver) => ({ ...driver }));

  return (
    <>
      <BaseModal
        titleIcon={action == 'add' ? <PersonAddOutlined /> : <Edit />}
        title={action == 'add' ? t('addNewDriver') : t('editDriverInfo')}
        open={openModal}
        onClose={() => setOpenModal(false)}
      >
        {getForm()}
      </BaseModal>
      <Stack alignItems="flex-end" className={addDriverButtonContainer}>
        <Button
          className={addDriverButton}
          variant="contained"
          onClick={openAddDriverModal}
          startIcon={<AddRounded />}
        >
          {t('addDriver')}
        </Button>
      </Stack>
      {isEmptyList && (
        <Typography className={emptyList} variant="body2">
          {t('emptyDriverList')}
        </Typography>
      )}
      {!isEmptyList && (
        <FilterableTable
          ariaLabel="drivers-list"
          columns={columns}
          rows={rows}
          paginationAndSortMode="client"
        />
      )}
    </>
  );
};

const DeleteDriverButton = ({
  clientId,
  driverId,
}: {
  clientId: string;
  driverId: string;
}) => {
  const [open, setOpen] = useState(false);
  const { showErrorToast, showSuccessToast } = useToast();

  const { deleteDriver, isLoading } = useDeleteDriver({
    clientId,
    driverId,
    onSuccess: () => {
      setOpen(false);
      showSuccessToast(t('deleteDriver.success'));
    },
    onError: () => {
      showErrorToast(t('deleteDriver.error'));
    },
  });

  return (
    <>
      <IconButton onClick={() => setOpen(true)} title={t('deleteDriver.title')}>
        <DeleteIcon />
      </IconButton>
      <BaseModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('deleteDriver.title')}
      >
        <Stack gap={theme.spacing['3']}>
          <Typography>{t('deleteDriver.message')}</Typography>
          <CloseConfirmModalFooter
            onClose={() => setOpen(false)}
            confirm={() => deleteDriver()}
            closeLabel={t('cancel')}
            confirmLabel={t('delete')}
            confirmLoading={isLoading}
            confirmDisabled={isLoading}
          />
        </Stack>
      </BaseModal>
    </>
  );
};

const emptyList = css({
  marginLeft: theme.spacing['4'],
});

const addDriverButtonContainer = css({
  marginBottom: theme.spacing['3'],
  marginTop: theme.spacing['3'],
});

const addDriverButton = css({
  backgroundColor: theme.palette.primary.primary,
  borderRadius: '8px',
});
